// Pure row-model for the design-system Table. The DS Table renders a flat
// list of primitive-ish row objects; this module maps an OpportunityEdge (a Project with status=OPPORTUNITY,
// as loaded through the ProjectsLoader_* fragments) into that flat shape, and owns the per-column sort
// comparators + the sort <-> sessionStorage translation. It carries the bulk of the feature's logic, so it
// is exhaustively unit-tested and kept free of any React / design-system imports.

import type {SortingFn} from '@tanstack/react-table';
import util from '../shared/util/util';
import {projectUrl, profilePicSrc} from '../../directApi';
import {deriveOpportunityStage, OPPORTUNITY_STAGE_MESSAGE_IDS, winProbabilityBand} from './opportunityStage';
import {deriveOpportunityState, OPPORTUNITY_TAB_MESSAGE_IDS} from './opportunityFilters';
import type {OpportunitySortKey, SortOrder} from './opportunityFilters';
import type {OpportunityEdge, OpportunityNode, OpportunityPerson} from './opportunityTypes';

// The four mutually-exclusive opportunity states (the filter tabs minus the "all" pseudo-tab).
export type OpportunityState = 'open' | 'won' | 'lost' | 'won_unconverted';

// A minimal intl surface — only formatMessage is needed to resolve the stage / status labels at map time
// (so the columns stay hook-free). Accepts the real react-intl IntlShape or a test stub.
export interface RowIntl {
	formatMessage: (descriptor: {id: string}) => string;
}

export interface OpportunityTableRow {
	id: string;
	customId: string; // "P42" — the colored id badge text
	projectColor: string; // badge background
	name: string;
	clientName: string; // '' when the opportunity has no client
	contactName: string; // '' when there is no primary contact
	contactEmail: string; // '' when the contact has no email
	contactImage?: string; // profile-picture src, undefined -> DS Avatar renders initials
	salespersonName: string; // '' when there is no salesperson
	salespersonImage?: string;
	targetValue: number | null; // baseline revenue; 0 / missing -> null -> DS CurrencyColumn renders a dash
	currency: string; // ISO code for the CurrencyColumn
	winChance: number; // 0..1, coalesced (DB default 1.00) — drives the band label, dot and stage
	// The value the Win Probability column sorts on: the raw win chance, or `undefined` when it was never
	// set. Kept separate from `winChance` (which coalesces unset to 1.00 for display) so the column can sort
	// unset rows to the bottom in BOTH directions via the DS Table's `sortUndefined: 'last'` — a plain
	// comparator can't, since the table flips its sign for descending. `null` would not trip that check;
	// only `undefined` does. 0% (Lost) stays a real value and sorts normally — it is set, not unset.
	winChanceSort: number | undefined;
	winBandLabel: string; // e.g. "40–59%"
	winBandColor: string; // traffic-light dot colour
	stageLabel: string; // localized Opportunity Stage
	statusState: OpportunityState | null; // Open / Won / Lost / Won-Unconverted, or null
	statusLabel: string; // localized status label ('' when stateless)
	startDate: Date | null; // for sorting
	startDateLabel: string | null; // preformatted display ('ll'); null -> "add dates" affordance
	endDate: Date | null;
	endDateLabel: string | null;
	quotePlaceholder: string; // dash placeholder for the two data-less Quote columns (matches the DS CurrencyColumn's missing-value dash used by Target Value)
	projectUrlPath: string; // /project/... target for the name-cell navigation
	node: OpportunityNode; // retained for the row actions (add-dates / delete)
}

// Em-dash shown in the Quote Status / Quote Expiry columns, which have no backing data yet.
export const QUOTE_PLACEHOLDER = '-';

const personName = (p?: OpportunityPerson | null): string => (p ? `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() : '');

const personImage = (p?: OpportunityPerson | null): string | undefined =>
	// profilePicSrc returns a URL string for any truthy id (only null for a falsy one, which the guard rules
	// out), so the cast is safe and avoids an unreachable nullish branch.
	p?.profilePictureId ? (profilePicSrc(p.profilePictureId) as string) : undefined;

export function toOpportunityTableRow(edge: OpportunityEdge, intl: RowIntl, currency: string): OpportunityTableRow {
	const node = edge.node;
	const band = winProbabilityBand(node.baselineWinChance);
	const state = deriveOpportunityState(node);

	const startMoment = util.CreateNonUtcMomentDate(node.projectStartYear, node.projectStartMonth, node.projectStartDay);
	const endMoment = util.CreateNonUtcMomentDate(node.projectEndYear, node.projectEndMonth, node.projectEndDay);

	return {
		id: node.id,
		// The project's custom ID when one is set, else the auto "P<companyProjectId>" — matching how the
		// project indicator is shown elsewhere in Forecast (global search, integrations).
		customId: node.customProjectId || `P${node.companyProjectId ?? ''}`,
		projectColor: node.projectColor || '#6b7280',
		name: node.name ?? '',
		clientName: node.client ? node.client.name : '',
		contactName: personName(node.clientContact),
		contactEmail: node.clientContact?.email ?? '',
		contactImage: personImage(node.clientContact),
		salespersonName: personName(node.salesperson),
		salespersonImage: personImage(node.salesperson),
		// A $0 target is treated as "not set" (like a missing value) → null so the column renders '-' rather
		// than a misleading $0.00; a real revenue keeps its value.
		targetValue: node.financialNumbers?.baselineRevenue || null,
		currency,
		winChance: node.baselineWinChance ?? 1,
		winChanceSort: node.baselineWinChance ?? undefined, // null/undefined (unset) -> undefined -> sorts last
		winBandLabel: band.label,
		winBandColor: band.color,
		stageLabel: intl.formatMessage({id: OPPORTUNITY_STAGE_MESSAGE_IDS[deriveOpportunityStage(node.baselineWinChance)]}),
		statusState: state,
		statusLabel: state ? intl.formatMessage({id: OPPORTUNITY_TAB_MESSAGE_IDS[state]}) : '',
		startDate: startMoment ? startMoment.toDate() : null,
		startDateLabel: startMoment ? startMoment.format('ll') : null,
		endDate: endMoment ? endMoment.toDate() : null,
		endDateLabel: endMoment ? endMoment.format('ll') : null,
		quotePlaceholder: QUOTE_PLACEHOLDER,
		projectUrlPath: projectUrl(node.companyProjectId, node.customProjectId),
		node,
	};
}

// Case-insensitive string comparison (mirrors the legacy sortOpportunities lowercasing so text columns keep
// ordering the way users expect regardless of capitalisation).
const cmpText = (a: string, b: string): number => {
	const la = a.toLowerCase();
	const lb = b.toLowerCase();
	if (la < lb) return -1;
	if (la > lb) return 1;
	return 0;
};

const timeOf = (d: Date | null): number => (d ? d.getTime() : 0);

// A comparator over the mapped rows, wrapped into the tanstack SortingFn shape (rowA, rowB) -> number that
// the DS Table expects. The exported type is SortingFn<any> so it satisfies the DS columns' generic
// SortingFnOption<any[]> prop (the DS Table types its data as any[]); the single wrapper keeps every column
// comparator strongly typed against OpportunityTableRow internally.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- match the DS Table's any-typed sortingFn slot
export type OpportunitySortingFn = SortingFn<any>;
type RowComparator = (a: OpportunityTableRow, b: OpportunityTableRow) => number;
const asSortingFn =
	(cmp: RowComparator): OpportunitySortingFn =>
	(a, b) =>
		cmp(a.original, b.original);

// One comparator per sortable column, keyed by the OpportunitySortKey the column advertises (Opportunity
// Stage and Win Probability both order by the underlying win chance, matching the legacy sortOpportunities).
const byWinChance: RowComparator = (a, b) => a.winChance - b.winChance;
export const opportunitySortingFns: Record<OpportunitySortKey, OpportunitySortingFn> = {
	name: asSortingFn((a, b) => cmpText(a.name, b.name)),
	client: asSortingFn((a, b) => cmpText(a.clientName, b.clientName)),
	contact: asSortingFn((a, b) => cmpText(a.contactName, b.contactName)),
	salesperson: asSortingFn((a, b) => cmpText(a.salespersonName, b.salespersonName)),
	targetValue: asSortingFn((a, b) => (a.targetValue ?? 0) - (b.targetValue ?? 0)),
	winChance: asSortingFn(byWinChance),
	stage: asSortingFn(byWinChance),
	startDate: asSortingFn((a, b) => timeOf(a.startDate) - timeOf(b.startDate)),
	endDate: asSortingFn((a, b) => timeOf(a.endDate) - timeOf(b.endDate)),
	status: asSortingFn((a, b) => cmpText(a.statusState ?? '', b.statusState ?? '')),
};

// The DS Table sorts internally and only accepts a seed (initialSorting) + emits changes (onSortingChange).
// The column ids are the OpportunitySortKeys themselves, so translation to/from the persisted {key, order}
// session shape is a direct 1:1 mapping. Kept pure + tested so the page holds no branchy sort glue.
export interface SessionSort {
	key: OpportunitySortKey;
	order: SortOrder;
}
export type SortingState = Array<{id: string; desc: boolean}>;

export function sessionSortToSortingState(sort: SessionSort | null | undefined): SortingState {
	return sort ? [{id: sort.key, desc: sort.order === 'desc'}] : [];
}

export function sortingStateToSessionSort(state: SortingState): SessionSort | null {
	if (!state.length) return null;
	const {id, desc} = state[0];
	return {key: id as OpportunitySortKey, order: desc ? 'desc' : 'asc'};
}
